import { Ban, Building2, ChevronLeft, Flag, Info, Layers, Pencil, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { RowActionsMenu, type RowAction } from '@/components/ui/row-actions-menu';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { checkVat, type Site, type Supplier, updateSupplier, type WriteOutcome } from './api';
import { countryName, DataCard } from './DataCard';
import { EditSupplierDialog } from './EditSupplierDialog';
import { FlagNotice } from './FlagNotice';
import { HistoryCard } from './HistoryCard';
import { formatMonth } from './UsesCard';
import { addressLine } from './maps';
import { Notice } from './Notice';
import { SupplierLink } from './panel-links';
import { ReportProblemSheet } from './ReportProblemSheet';
import { SiteDialog } from './SiteDialog';
import { sitesOf, SitesCard } from './SitesCard';
import { SupplierMarks } from './SupplierMarks';
import { UsesCard } from './UsesCard';
import { useSupplier } from './use-supplier';
import { VatProposalNotice } from './VatProposalNotice';

/** Where the page goes back to: the «Fornitori» list (#958 piece H). */
const LIST_PATH = '/expenses/suppliers';

/** The problems that mean «someone changed it meanwhile»: the page reloads what is true now. */
const STALE = new Set(['VERSION_CONFLICT', 'PROPOSAL_DECIDED', 'FLAG_RESOLVED', 'PROPOSAL_STALE']);

/**
 * The supplier page (#958 piece G; report §3.4, boards 3.1–3.8): the registry data, the sites,
 * the expenses with the supplier and the page's own history; no map (U16): an address opens the
 * device's maps app and can be copied. The status and its marks, with their ways out for whoever
 * the API's `can` allows; a colleague's view per §3.10; «Segnala un problema» for everyone.
 */
export function SupplierPage() {
  const { t } = useTranslation('suppliers');
  const { supplierId = '' } = useParams();
  const { state, reload, replace } = useSupplier(supplierId);

  return (
    <section className="flex flex-col gap-4">
      <Link
        to={LIST_PATH}
        className="text-muted-foreground hover:text-foreground inline-flex min-h-11 w-fit items-center gap-1 text-sm"
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
        {t('page.back')}
      </Link>
      {state.status === 'loading' ? (
        <SkeletonRegion label={t('page.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </SkeletonRegion>
      ) : null}
      {state.status === 'not-found' ? (
        <StateMessage
          icon={Building2}
          title={t('page.notFound.title')}
          text={t('page.notFound.text')}
          action={
            <Button asChild variant="outline">
              <Link to={LIST_PATH}>{t('page.back')}</Link>
            </Button>
          }
        />
      ) : null}
      {state.status === 'error' ? (
        <StateMessage
          role="alert"
          tone="error"
          icon={Building2}
          title={t('page.loadError')}
          action={
            <Button variant="outline" onClick={reload}>
              {t('page.retry')}
            </Button>
          }
        />
      ) : null}
      {state.status === 'ready' ? (
        <SupplierBody supplier={state.supplier} onReload={reload} onReplace={replace} />
      ) : null}
    </section>
  );
}

/**
 * The supplier's parts: on its own page, or in the side panel a form opens on it (U1). The panel
 * keeps the data, the sites, the expenses with the supplier and the history, with «Modifica»,
 * «Completa», the sites' editing and the other names' «×» for whoever `can` allows (U2); the head's
 * «Segnala un problema» and «···», and Gestione's notices, stay on the page.
 */
export function SupplierBody({
  supplier,
  onReload,
  onReplace,
  variant = 'page',
}: {
  readonly supplier: Supplier;
  readonly onReload: () => void;
  readonly onReplace: (supplier: Supplier) => void;
  readonly variant?: 'page' | 'panel';
}) {
  const { t, i18n } = useTranslation('suppliers');
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  // The site form: open on a site to change it, or on null to add one.
  const [siteForm, setSiteForm] = useState<{ readonly site: Site | null } | null>(null);
  // Every write the page makes moves «Storia della scheda».
  const [revision, setRevision] = useState(0);

  function onWrite(outcome: WriteOutcome, done: string) {
    setEditing(false);
    setReporting(false);
    setSiteForm(null);
    if (outcome.ok) {
      onReplace(outcome.supplier);
      setRevision((count) => count + 1);
      toast.success(done);
      return;
    }
    if (outcome.status === 409 && outcome.code !== null && STALE.has(outcome.code)) {
      toast.warning(t(outcome.code === 'PROPOSAL_STALE' ? 'verify.stale' : 'page.conflict'));
      onReload();
      return;
    }
    toast.error(outcome.message ?? t('page.writeFailed'));
  }

  const language = i18n.resolvedLanguage ?? 'it';
  const place = [supplier.registeredOffice?.address?.city, countryName(supplier.country, language)]
    .filter(Boolean)
    .join(', ');
  const subtitle = [supplier.legalName !== supplier.name ? supplier.legalName : null, place]
    .filter(Boolean)
    .join(' · ');
  const readOnly = supplier.status === 'merged';
  const panel = variant === 'panel';

  return (
    <>
      {panel ? (
        subtitle ? (
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        ) : null
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="screen-title">{supplier.name}</h2>
            {subtitle ? <p className="text-muted-foreground text-sm">{subtitle}</p> : null}
          </div>
          {readOnly ? null : (
            <HeadActions
              supplier={supplier}
              onEdit={() => setEditing(true)}
              onReport={() => setReporting(true)}
              onWrite={onWrite}
            />
          )}
        </div>
      )}
      <SupplierMarks supplier={supplier} />
      <StateNotices
        supplier={supplier}
        onEdit={() => setEditing(true)}
        canEdit={supplier.can.edit && !readOnly}
      />
      {panel ? null : <VatProposalNotice supplier={supplier} onWrite={onWrite} />}
      {panel ? null : <FlagNotice supplier={supplier} onWrite={onWrite} />}
      <Stats supplier={supplier} language={language} />
      <div className={panel ? 'flex flex-col gap-4' : 'grid gap-4 @min-[900px]/shell:grid-cols-2'}>
        <div className="flex min-w-0 flex-col gap-4">
          <DataCard
            supplier={supplier}
            {...(supplier.can.edit && !readOnly ? { onEdit: () => setEditing(true) } : {})}
            onWrite={onWrite}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <SitesCard
            supplier={supplier}
            {...(supplier.can.addSite && !readOnly
              ? { onAdd: () => setSiteForm({ site: null }) }
              : {})}
            {...(supplier.can.edit && !readOnly
              ? { onEdit: (site: Site) => setSiteForm({ site }) }
              : {})}
          />
          <HistoryCard supplierId={supplier.id} revision={revision} />
        </div>
      </div>
      <UsesCard supplier={supplier} />
      {supplier.can.edit && !readOnly ? (
        <EditSupplierDialog
          open={editing}
          onOpenChange={setEditing}
          supplier={supplier}
          onDone={onWrite}
        />
      ) : null}
      {(supplier.can.addSite || supplier.can.edit) && !readOnly ? (
        <SiteDialog
          open={siteForm !== null}
          onOpenChange={(open) => (open ? undefined : setSiteForm(null))}
          supplier={supplier}
          site={siteForm?.site ?? null}
          onDone={onWrite}
        />
      ) : null}
      {supplier.can.flag && !readOnly && !panel ? (
        <ReportProblemSheet
          open={reporting}
          onOpenChange={setReporting}
          supplier={supplier}
          onDone={onWrite}
        />
      ) : null}
    </>
  );
}

/** «Modifica», «Segnala un problema», and Gestione's «···»: only what `can` allows. */
function HeadActions({
  supplier,
  onEdit,
  onReport,
  onWrite,
}: {
  readonly supplier: Supplier;
  readonly onEdit: () => void;
  readonly onReport: () => void;
  readonly onWrite: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const [busy, setBusy] = useState(false);

  async function run(write: () => Promise<WriteOutcome>, done: string) {
    if (busy) return;
    setBusy(true);
    const outcome = await write();
    setBusy(false);
    onWrite(outcome, done);
  }

  const more: RowAction[] = [];
  if (supplier.can.checkVat && supplier.vat) {
    more.push({
      id: 'check-vat',
      label: t('actions.checkVat'),
      icon: ShieldCheck,
      busy,
      onSelect: () => void run(() => checkVat(supplier.id), t('actions.vatChecked')),
    });
  }
  if (supplier.can.setStatus) {
    more.push({
      id: 'intermediary',
      label: t(supplier.intermediary ? 'actions.notIntermediary' : 'actions.intermediary'),
      icon: Layers,
      busy,
      onSelect: () =>
        void run(
          () => updateSupplier(supplier, { intermediary: !supplier.intermediary }),
          t('page.saved'),
        ),
    });
    more.push(
      supplier.status === 'active'
        ? {
            id: 'unusable',
            label: t('actions.makeUnusable'),
            icon: Ban,
            tone: 'destructive',
            busy,
            onSelect: () =>
              void run(() => updateSupplier(supplier, { status: 'unusable' }), t('page.saved')),
          }
        : {
            id: 'usable',
            label: t('actions.makeUsable'),
            icon: ShieldCheck,
            busy,
            onSelect: () =>
              void run(() => updateSupplier(supplier, { status: 'active' }), t('page.saved')),
          },
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {supplier.can.edit ? (
        <Button variant="outline" onClick={onEdit}>
          <Pencil aria-hidden="true" />
          {t('actions.edit')}
        </Button>
      ) : null}
      {supplier.can.flag ? (
        <Button variant="outline" onClick={onReport}>
          <Flag aria-hidden="true" />
          {t('actions.report')}
        </Button>
      ) : null}
      <RowActionsMenu label={t('actions.more', { name: supplier.name })} actions={more} />
    </div>
  );
}

/** The notices of the status and the marks that ask nothing of Gestione's buttons. */
function StateNotices({
  supplier,
  onEdit,
  canEdit,
}: {
  readonly supplier: Supplier;
  readonly onEdit: () => void;
  readonly canEdit: boolean;
}) {
  const { t } = useTranslation('suppliers');
  const missingVat = !supplier.vat;
  const missingOffice = addressLine(supplier.registeredOffice?.address) === null;
  return (
    <>
      {supplier.status === 'merged' && supplier.mergedInto ? (
        <Notice tone="neutral" icon={Info} title={t('state.merged')}>
          <SupplierLink
            supplierId={supplier.mergedInto.id}
            className="text-link font-semibold hover:underline"
          >
            {supplier.mergedInto.name}
          </SupplierLink>
        </Notice>
      ) : null}
      {supplier.status === 'unusable' ? (
        <Notice tone="neutral" icon={Ban} title={t('state.unusable')}>
          <p>{t('state.unusableText')}</p>
        </Notice>
      ) : null}
      {supplier.completeness === 'to_complete' && canEdit ? (
        <Notice
          tone="warning"
          icon={Info}
          title={t(
            missingVat && missingOffice
              ? 'toComplete.both'
              : missingVat
                ? 'toComplete.vat'
                : 'toComplete.office',
          )}
          actions={
            missingVat ? (
              <Button size="sm" onClick={onEdit}>
                <Pencil aria-hidden="true" />
                {t('toComplete.action')}
              </Button>
            ) : null
          }
        >
          <p>{t(supplier.createdByMe ? 'toComplete.yours' : 'toComplete.others')}</p>
        </Notice>
      ) : null}
      {supplier.intermediary ? (
        <Notice tone="info" icon={Layers} title={t('intermediary.title')}>
          <p>{t('intermediary.text')}</p>
        </Notice>
      ) : null}
    </>
  );
}

/** «14 usi · ottobre 2026 ultimo uso · 2 sedi» (board 3.1): everyone sees them (§3.10). */
function Stats({ supplier, language }: { readonly supplier: Supplier; readonly language: string }) {
  const { t } = useTranslation('suppliers');
  const sites = sitesOf(supplier).filter((site) => site.active).length;
  const items = [
    { value: String(supplier.uses), label: t('stats.uses', { count: supplier.uses }) },
    {
      value: supplier.lastUsed ? formatMonth(supplier.lastUsed, language) : '—',
      label: t('stats.lastUsed'),
    },
    { value: String(sites), label: t('stats.sites', { count: sites }) },
  ];
  return (
    <dl className="flex flex-wrap gap-x-8 gap-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col-reverse">
          <dt className="text-muted-foreground text-xs">{item.label}</dt>
          <dd className="text-lg font-bold tabular-nums">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
